import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import paragonLogo from "@/assets/paragon-logo-violet.svg";
import paragonSymbol from "@/assets/paragon-symbol-violet.svg";

const navItems = [
  { label: "Home", href: "#home" },
  { label: "Partners", href: "#partners" },
  { label: "Services", href: "#services" },
  { label: "Broadcast", href: "#broadcast" },
  { label: "News", href: "#news" },
  { label: "About", href: "/about", isPage: true },
];

export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    // Force dark mode
    document.documentElement.classList.add("dark");
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-background/80 backdrop-blur-xl border-b border-border shadow-lg"
          : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <a
            href="/"
            className="flex items-center gap-2 hover:opacity-80 transition-all duration-300 relative overflow-hidden"
            aria-label="Go to home page"
          >
            <div className="relative h-10 lg:h-12 flex items-center">
              <img 
                src={paragonSymbol} 
                alt="Paragon" 
                className={`h-10 w-10 lg:h-12 lg:w-12 transition-all duration-500 ${
                  isScrolled ? 'opacity-0 scale-75' : 'opacity-100 scale-100'
                }`}
              />
              <img 
                src={paragonLogo} 
                alt="Paragon" 
                className={`h-10 w-auto absolute left-0 transition-all duration-500 ${
                  isScrolled ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
                }`}
              />
            </div>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              item.isPage ? (
                <a
                  key={item.href}
                  href={item.href}
                  className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors relative group"
                >
                  {item.label}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full" />
                </a>
              ) : (
                <button
                  key={item.href}
                  onClick={() => scrollToSection(item.href)}
                  className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors relative group"
                >
                  {item.label}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full" />
                </button>
              )
            ))}
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center gap-2 lg:gap-4">
            {/* Desktop CTA */}
            <Button
              asChild
              className="hidden lg:inline-flex h-10 px-5 bg-primary hover:bg-primary-glow text-primary-foreground"
            >
              <a href="mailto:info@paragonevents.gg">Contact Us</a>
            </Button>

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-background/95 backdrop-blur-xl border-b border-border">
          <div className="container mx-auto px-4 py-6 space-y-4">
            {navItems.map((item) => (
              item.isPage ? (
                <a
                  key={item.href}
                  href={item.href}
                  className="block w-full text-left text-base font-medium text-foreground/80 hover:text-primary transition-colors py-2"
                >
                  {item.label}
                </a>
              ) : (
                <button
                  key={item.href}
                  onClick={() => scrollToSection(item.href)}
                  className="block w-full text-left text-base font-medium text-foreground/80 hover:text-primary transition-colors py-2"
                >
                  {item.label}
                </button>
              )
            ))}
            <Button
              asChild
              className="w-full h-10 bg-primary hover:bg-primary-glow text-primary-foreground"
            >
              <a href="mailto:info@paragonevents.gg">Contact Us</a>
            </Button>
          </div>
        </div>
      )}
    </nav>
  );
};
